import React, { useEffect } from 'react';
import videojs from 'video.js';
import 'videojs-contrib-ads';
import 'videojs-ima';

import initializeAds from './utils/initializeAds';
import initializeEventListeners from './utils/initializeEventListeners';

import 'video.js/dist/video-js.css';
import 'videojs-ima/dist/videojs.ima.css';

import './Video.scss';

/**
 * Map a media URL to an explicit MIME type for the <source> element. video.js needs
 * this to pick the right playback engine — most importantly `application/x-mpegURL`
 * so its HLS engine (VHS) handles our transcoded `.m3u8` streams in browsers that
 * don't support HLS natively (Chrome/Firefox). Progressive containers get their
 * own type so the browser can play them directly (PB-37963).
 */
function getVideoSourceType(url = '') {
  const path = url.split('?')[0].toLowerCase();
  if (path.endsWith('.m3u8')) return 'application/x-mpegURL';
  if (path.endsWith('.mpd')) return 'application/dash+xml';
  if (path.endsWith('.webm')) return 'video/webm';
  if (path.endsWith('.ogv') || path.endsWith('.ogg')) return 'video/ogg';
  // .mov / .mp4 / .m4v and anything else claim progressive mp4. QuickTime shares
  // the ISO-BMFF container family, so browsers' mp4 pipeline demuxes H.264 .mov
  // files fine — whereas the honest 'video/quicktime' makes canPlayType() return
  // "" and video.js hard-rejects the source before even trying, showing "No
  // compatible source was found for this media". Original files only play
  // pre-transcode anyway; once MediaConvert finishes, the URL is the .m3u8 stream.
  return 'video/mp4';
}

export default function Video({
  videoUrl = '',
  poster = '',
  adTagUrl = '',
  playerOptions = {},
  onAdEvent = null,
  onReady = null,
  onDisposed = null,
  onPlay = null,
  onPause = null,
  onWaiting = null,
  onPlaying = null,
  onTimeUpdate = null,
  onSeeking = null,
  onSeeked = null,
  onEnded = null,
  onError = null,
  onLoadedData = null,
  onLoadedMetadata = null,
}) {
  const playerElementId = 'content_video';

  const options = {
    autoplay: true,
    muted: true,
    playsinline: true,
    controls: true,
    responsive: true,
    fluid: true,
    playbackRates: [1, 1.5, 2],
    controlBar: {
      children: [
        'playToggle',
        'volumePanel',
        'currentTimeDisplay',
        'timeDivider',
        'durationDisplay',
        'progressControl',
        'playbackRateMenuButton',
        'pictureInPictureToggle',
        'fullscreenToggle',
      ],
      volumePanel: { inline: true },
    },
    ...playerOptions,
  };

  useEffect(() => {
    const player = videojs(playerElementId, options);

    initializeAds(player, adTagUrl, onAdEvent);

    player.on('ready', () => {
      // Click-to-cycle speed instead of dropdown menu
      const rateButton = player.controlBar.getChild('playbackRateMenuButton');
      if (rateButton) {
        rateButton.on('click', () => {
          const rates = player.playbackRates();
          const current = player.playbackRate();
          const idx = rates.indexOf(current);
          const next = rates[(idx + 1) % rates.length];
          player.playbackRate(next);
        });
      }

      initializeEventListeners(player, {
        onEnded,
        onError,
        onLoadedData,
        onLoadedMetadata,
        onPause,
        onPlay,
        onPlaying,
        onSeeked,
        onSeeking,
        onTimeUpdate,
        onWaiting,
      });

      onReady?.(player);
    });

    return () => {
      onDisposed?.(player, player.currentTime());
      player.dispose();
    };
  }, []);

  return (
    <div data-vjs-player>
      <video id={playerElementId} className="video-js vjs-big-play-centered" playsInline poster={poster || undefined}>
        <source src={videoUrl} type={getVideoSourceType(videoUrl)} />
      </video>
    </div>
  );
}
